/**
 * Avatar — circular user/identity badge.
 *
 * Wraps Radix Avatar so we get image fallback handling (e.g. shows initials
 * if the image 404s). Default uses the Aurora gradient as the fallback fill,
 * keeping the brand visible even before any user image loads.
 */
import * as RadixAvatar from "@radix-ui/react-avatar";
import { type ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/cn";

export interface AvatarProps
  extends ComponentPropsWithoutRef<typeof RadixAvatar.Root> {
  src?: string;
  alt?: string;
  /** Two-letter initials shown when no image is available. */
  initials?: string;
  size?: "sm" | "md" | "lg";
}

const sizeMap = {
  sm: "h-7 w-7 text-[10px]",
  md: "h-9 w-9 text-xs",
  lg: "h-12 w-12 text-sm",
} as const;

export function Avatar({
  src,
  alt,
  initials = "?",
  size = "md",
  className,
  ...props
}: AvatarProps) {
  return (
    <RadixAvatar.Root
      className={cn(
        "inline-flex items-center justify-center rounded-full overflow-hidden",
        "bg-aurora-grad text-white font-bold",
        "shadow-[inset_0_1px_0_rgba(255,255,255,0.4)]",
        sizeMap[size],
        className,
      )}
      {...props}
    >
      {src ? (
        <RadixAvatar.Image src={src} alt={alt} className="h-full w-full object-cover" />
      ) : null}
      <RadixAvatar.Fallback delayMs={src ? 400 : 0}>{initials}</RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
}
